// AuditScreen.tsx — the cross-tenant operator action log.
//
// GET /platform/audit exists and returns operator_audit, which is append-only and
// NOT tenant-scoped, so an operator sees the full cross-tenant history. The
// wiring doc lists Audit as needing a new endpoint; it does not — the endpoint
// was added and this screen binds to it. Server caps the page at 500.
//
// FILTERING IS CLIENT-SIDE, deliberately and visibly. The endpoint supports only
// `tenant_id` and `limit`. Free-text and action filters therefore narrow *the
// fetched page*, not the whole log — so the screen says which of the two it is
// doing. A filter box that silently searches only the last 500 rows while looking
// like it searched everything is how an investigation reaches a wrong conclusion.
//
// Export writes the FILTERED, FETCHED rows. Same caveat, stated on the button.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PT } from "../../platformTheme";
import { pget, perr } from "./api";
import {
  PCard, PSection, PBtn, PBadge, PBanner, PEmpty, PSpinner, PTable, PRow,
  PInput, PSelect, PId, th, td, fmtDate,
} from "./ui";

const RANGES = [
  { value: "1", label: "24 hours" },
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "0", label: "All fetched" },
];

const LIMIT = 500;

export const AuditScreen: React.FC<{ tenants?: { tenant_id: string; name: string }[] }> = ({ tenants = [] }) => {
  const [rows, setRows] = useState<any[] | null>(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [range, setRange] = useState("7");
  const [tenantId, setTenantId] = useState("");

  const load = useCallback(async () => {
    setErr(""); setRows(null);
    const r = await pget("/audit", { limit: LIMIT, tenant_id: tenantId || undefined });
    if (!r.ok) { setErr(perr(r, "Could not load the audit log")); setRows([]); return; }
    setRows(r.data?.entries || []);
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const actions = useMemo(
    () => Array.from(new Set((rows || []).map((e) => e.action).filter(Boolean))).sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const days = Number(range);
    const cut = days > 0 ? Date.now() - days * 864e5 : 0;
    return (rows || []).filter((e) => {
      if (action && e.action !== action) return false;
      if (cut && new Date(e.occurred_at).getTime() < cut) return false;
      if (!s) return true;
      return [e.action, e.reason, e.reference, e.target_table, e.target_tenant_id, e.actor_user_id]
        .filter(Boolean).some((v: string) => String(v).toLowerCase().includes(s));
    });
  }, [rows, q, action, range]);

  const exportCsv = () => {
    const cols = ["occurred_at", "action", "actor_user_id", "actor_type", "target_tenant_id", "target_table", "target_id", "reason", "reference"];
    const esc = (v: any) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [cols.join(","), ...filtered.map((e) => cols.map((c) => esc(e[c])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `operator-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const truncated = (rows || []).length >= LIMIT;

  return (
    <PSection
      title="Audit"
      sub={rows ? `${filtered.length} shown of ${rows.length} fetched` : undefined}
      right={
        <>
          <PBtn small onClick={load}>Refresh</PBtn>{" "}
          <PBtn
            small
            disabled={!filtered.length}
            onClick={exportCsv}
            title="Exports the rows currently shown — the filtered subset of the fetched page, not the whole log"
          >Export CSV</PBtn>
        </>
      }
    >
      <PBanner>{err}</PBanner>

      {truncated && (
        <PBanner tone="warn">
          Showing the most recent <strong>{LIMIT}</strong> entries — the server's maximum page. Filters
          below narrow <strong>this page only</strong>. Narrow by customer to reach further back.
        </PBanner>
      )}

      <PCard style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
          <div style={{ marginBottom: -12 }}>
            <PInput label="Filter" value={q} onChange={setQ} placeholder="action, reason, id…" />
          </div>
          <div style={{ marginBottom: -12 }}>
            <PSelect
              label="Action" value={action} onChange={setAction}
              options={[{ value: "", label: "All actions" }, ...actions.map((a) => ({ value: a, label: a }))]}
            />
          </div>
          <div style={{ marginBottom: -12 }}>
            <PSelect label="Range" value={range} onChange={setRange} options={RANGES} />
          </div>
          <div style={{ marginBottom: -12 }}>
            <PSelect
              label="Customer" value={tenantId} onChange={setTenantId}
              options={[{ value: "", label: "All customers" }, ...tenants.map((t) => ({ value: t.tenant_id, label: t.name }))]}
              hint="Applied server-side"
            />
          </div>
        </div>
      </PCard>

      <PCard style={{ padding: 0 }}>
        {rows === null ? <PSpinner /> : filtered.length === 0 ? (
          <PEmpty>{(rows || []).length === 0 ? "No operator actions recorded." : "No entry matches these filters."}</PEmpty>
        ) : (
          <PTable
            minWidth={880}
            head={<>
              <th style={th}>Time</th><th style={th}>Operator</th><th style={th}>Customer</th>
              <th style={th}>Action</th><th style={th}>Target</th><th style={th}>Reason</th>
            </>}
          >
            {filtered.map((e) => (
              <PRow key={e.id}>
                <td style={{ ...td, whiteSpace: "nowrap", color: PT.txM, fontSize: 11.5 }}>{fmtDate(e.occurred_at)}</td>
                <td style={td}><PId id={e.actor_user_id} /></td>
                <td style={td}><PId id={e.target_tenant_id} /></td>
                <td style={td}><PBadge tone="info">{e.action}</PBadge></td>
                <td style={{ ...td, fontSize: 11.5, color: PT.txM }}>{e.target_table || "—"}</td>
                <td style={{ ...td, color: PT.txM }}>{e.reason || <span style={{ color: PT.txD }}>—</span>}</td>
              </PRow>
            ))}
          </PTable>
        )}
      </PCard>
    </PSection>
  );
};
